import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Loader2, Search, X, Check, AlertTriangle, ExternalLink, Clock,
  PackageOpen, Lock, RotateCcw,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { ItemArt, fmtFull, RARITY } from './ItemVisuals';
import { MmCoinIcon } from '../../context/AuthContext';
/* 🔪 Geri butonu ve MM2 logosu TEK KAYNAKTAN — bkz. Mm2Brand. */
import { BackButton, Mm2Logo } from './Mm2Brand';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const API = `${BACKEND_URL}/api`;

/* ============================================================================
   MM2 ÇEKİM — MM Coin ile bot stoğundan eşya al

   ⚠️ TEK KOLON. Önceki hâli `lg:grid-cols-[1fr_236px]` ile iki kolondu, ama
      cüzdan penceresi lg'de yalnızca 672px: kartlar ~85px'e sıkışıyordu.
      Seçim özeti artık alta dokunuyor — dar kapsayıcıda doğru desen bu.
============================================================================ */

const FALLBACK_MAX = 4;
const RBX_RE = /^[A-Za-z0-9_]{3,20}$/;

const mmss = (s) => {
  const n = Math.max(0, Math.floor(s));
  return `${Math.floor(n / 60)}:${String(n % 60).padStart(2, '0')}`;
};

const useCountdown = (deadline) => {
  const [left, setLeft] = useState(null);
  useEffect(() => {
    if (!deadline) { setLeft(null); return undefined; }
    const tick = () => setLeft(Math.max(0,
      Math.floor((new Date(deadline).getTime() - Date.now()) / 1000)));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [deadline]);
  return left;
};

/* ---------------------------------------------------------- MAĞAZA KARTI */
/* ⚠️ NADİRLİK RENGİ BU KARTTAN KALDIRILDI.
   Her kartın altında `inset 0 -20px 24px -22px <nadirlik rengi>` ile renkli
   bir parıltı vardı. Tek başına şıktı; ama ızgarada 20-30 kart yan yana
   gelince ekran dokuz farklı renkte çizgiye bölünüyor ve göz eşyalara değil
   renklere takılıyordu. Nadirlik zaten karar için gereken bilgi DEĞİL —
   kullanıcı fiyata ve eşyanın kendisine bakıyor, ikisi de kartta duruyor.
   Renk yalnızca görselin yüklenemediği durumda, monogramın arkasında
   kalıyor (bkz. ItemVisuals/ItemArt): orada ayırt edici bir işlevi var. */
const ShopCard = React.memo(({ item, count, disabled, onPick, affordable }) => {
  const out = (item?.stock || 0) <= 0;
  const dead = disabled || out;
  const picked = count > 0;

  return (
    <button
      type="button" disabled={dead}
      onClick={dead ? undefined : () => onPick?.(item.item_id)}
      data-testid={`mm2-shop-card-${item.item_id}`}
      title={`${item.name} — ${fmtFull(item.price)}`}
      className={`group relative rounded-lg border p-1.5 text-left transition-colors duration-150
        focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B059E8]
        ${picked ? 'border-[#2ecf6e] bg-[#2ecf6e]/[0.08]'
                 : dead ? 'border-[#22252a] bg-[#101215] opacity-40 cursor-not-allowed'
                        : 'border-[#2A2D31] bg-[#15171a] hover:border-[#3d424a]'}
        ${!affordable && !picked && !dead ? 'opacity-50' : ''}`}
    >
      {picked && (
        <span className="absolute top-1 right-1 z-10 min-w-[18px] h-[18px] px-1 rounded-full
                         bg-[#2ecf6e] grid place-items-center">
          {count > 1
            ? <span className="text-[10px] font-black text-black leading-none">{count}</span>
            : <Check className="w-3 h-3 text-black" strokeWidth={3.5} />}
        </span>
      )}
      {!picked && (item?.stock || 0) > 1 && (
        <span className="absolute top-1 right-1 z-10 text-[9px] font-black px-1 py-0.5
                         rounded bg-[#0E0F11]/85 text-zinc-400 border border-[#2A2D31]">
          {item.stock}
        </span>
      )}

      <div className="aspect-square w-full mb-1 grid place-items-center p-0.5">
        <ItemArt item={item} />
      </div>

      <p className="text-[10px] font-bold text-white leading-tight truncate">{item.name}</p>
      <p className="mt-0.5 inline-flex items-center gap-1 mm-num font-bold text-[10px]
                    text-[#2ecf6e]">
        {fmtFull(item.price)}<MmCoinIcon size={10} />
      </p>

      {out && (
        <span className="absolute inset-0 grid place-items-center rounded-lg bg-[#0E0F11]/75
                         text-[10px] font-black text-zinc-400 uppercase tracking-wider">
          Sold out
        </span>
      )}
    </button>
  );
});

/* ⚠️ TAKASI KULLANICI AÇAR. Bot gelen isteği kabul edip eşyaları ekliyor;
   "bot sana istek gönderecek" demek kullanıcıyı boşuna bekletiyordu. */
const STEPS = [
  'Join the bot\'s server',
  'Send the bot a trade request',
  'Bot adds your items — accept it',
];

const StepList = ({ active }) => (
  <ol className="space-y-2">
    {STEPS.map((label, i) => {
      const done = i < active, now = i === active;
      return (
        <li key={i} className="flex items-start gap-2.5 text-xs">
          <span className={`w-5 h-5 rounded-full grid place-items-center shrink-0 mt-px
            text-[10px] font-black
            ${done ? 'bg-[#2ecf6e]/15 text-[#2ecf6e]'
                   : now ? 'bg-[#15913f]/25 text-[#2ecf6e] ring-1 ring-[#16A34A]/40'
                         : 'bg-[#15171a] border border-[#2A2D31] text-zinc-500'}`}>
            {done ? <Check className="w-3 h-3" strokeWidth={3} /> : i + 1}
          </span>
          <span className={done ? 'text-zinc-500 line-through decoration-zinc-700'
                                : now ? 'text-white font-semibold' : 'text-zinc-400'}>
            {label}
          </span>
        </li>
      );
    })}
  </ol>
);

/* ============================================================== ANA PANEL */
export const Mm2WithdrawPanel = ({ user, token, cfg, onDone, onBack }) => {
  const auth = useMemo(
    () => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  const [shop, setShop] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [rarity, setRarity] = useState('all');
  const [picked, setPicked] = useState([]);
  const [name, setName] = useState('');
  /* Kullanıcının siteye bağlı Roblox hesabı. Girilen ad bundan SAPARSA
     alanın üstünde uyarı çıkar — sessizce başka bir hesaba göndermek
     geri alınamayan bir hata. */
  const linkedName = String(user?.roblox_username || user?.site_username || '').trim();
  const [submitting, setSubmitting] = useState(false);
  const [session, setSession] = useState(null);
  const [busy, setBusy] = useState(false);

  const balance = Math.floor(Number(user?.balance) || 0);
  const MAX_PICK = Math.max(1, Number(shop?.max_items) || FALLBACK_MAX);

  useEffect(() => {
    if (name) return;
    setName(String(user?.roblox_username || user?.site_username || '').slice(0, 20));
  }, [user?.roblox_username, user?.site_username]); // eslint-disable-line

  const loadShop = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/game/mm2/shop`, auth);
      setShop(r.data || null);
    } catch { setShop(null); }
    finally { setLoading(false); }
  }, [auth]);

  const loadSession = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/game/mm2/session`, auth);
      setSession(r.data?.session || null);
    } catch { /* sessiz */ }
  }, [auth]);

  useEffect(() => { loadShop(); loadSession(); }, [loadShop, loadSession]);
  useEffect(() => {
    if (!session) return undefined;
    const id = setInterval(loadSession, 3000);
    return () => clearInterval(id);
  }, [session, loadSession]);

  const prev = useRef(null);
  useEffect(() => {
    if (prev.current === true && !session) { onDone?.(); loadShop(); }
    prev.current = !!session;
  }, [session]); // eslint-disable-line

  const items = shop?.items || [];

  /* Pahalıdan ucuza — kullanıcı mağazaya "en iyi ne var" diye bakar. */
  const list = useMemo(() => {
    const t = q.trim().toLowerCase();
    return items
      .filter(i => rarity === 'all' || String(i.rarity).toLowerCase() === rarity)
      .filter(i => !t || String(i.name).toLowerCase().includes(t))
      .slice().sort((a, b) => (Number(b.price) || 0) - (Number(a.price) || 0));
  }, [items, q, rarity]);

  const rarities = useMemo(() => {
    const seen = new Set(items.map(i => String(i.rarity).toLowerCase()));
    return Object.keys(RARITY).filter(k => seen.has(k));
  }, [items]);

  const byId = useMemo(() => {
    const m = {}; items.forEach(i => { m[i.item_id] = i; }); return m;
  }, [items]);

  const total = useMemo(
    () => picked.reduce((s, id) => s + (Number(byId[id]?.price) || 0), 0), [picked, byId]);

  const countOf = useCallback((id) => picked.filter(x => x === id).length, [picked]);

  /* ⚠️ TIKLAMA DÖNGÜSÜ: 0 → 1 → … → tavan → 0. Tavanda "bir tane çıkar"
     yapılsaydı stok 2 olan eşyada seçim 1↔2 arasında salınır ve sıfıra
     inemezdi — yanlış seçilen eşya karttan kaldırılamazdı. */
  const toggle = useCallback((id) => {
    setPicked(cur => {
      const n = cur.filter(x => x === id).length;
      const cap = Math.min(Number(byId[id]?.stock) || 0, MAX_PICK);
      if (cur.length >= MAX_PICK && n > 0) return cur.filter(x => x !== id);
      if (n >= cap) return cur.filter(x => x !== id);
      if (cur.length >= MAX_PICK) {
        toast.error(`Up to ${MAX_PICK} items per withdrawal.`);
        return cur;
      }
      return [...cur, id];
    });
  }, [byId, MAX_PICK]);

  const paused = shop?.withdraw_paused === true;
  const locked = Math.max(0, Number(shop?.wager_required?.total) || 0);
  const free = Math.max(0, balance - locked);
  const enough = total <= free;
  const nameOk = RBX_RE.test(name.trim());
  const markup = Number(cfg?.balance_rail?.shop_markup_pct) || 0;

  const submit = async () => {
    if (!picked.length) return;
    if (!nameOk) { toast.error('Enter a valid Roblox username.'); return; }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/game/mm2/shop/withdraw`,
        { items: picked, roblox_username: name.trim() }, auth);
      if (r.data?.success) {
        setPicked([]);
        await Promise.all([loadSession(), loadShop()]);
        onDone?.();
      }
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Could not start the withdrawal.');
      loadShop();   /* en sık hata "stok bitti" — listeyi tazele */
    } finally { setSubmitting(false); }
  };

  const cancel = async (id) => {
    setBusy(true);
    try {
      await axios.post(`${API}/game/mm2/trade/cancel`, { trade_id: id }, auth);
      await Promise.all([loadSession(), loadShop()]);
      onDone?.();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Could not cancel.');
      loadSession();
    } finally { setBusy(false); }
  };

  const step = session?.active_step;
  const left = useCountdown(step?.join_deadline);
  const expired = step ? (step.join_expired || left === 0) : false;

  /* ═══════════════════════════════ AKTİF İŞLEM ═════════════════════════ */
  if (step && step.type === 'withdraw') {
    return (
      <div className="space-y-4" data-testid="mm2-wd-active">
        {/* Aktif işlemde de AYNI satır düzeni: sol başlık, sağ sayaç.
            ⚠️ Geri butonu BURADA YOK ve bu bilinçli — yarım kalmış bir
            takasın ortasında kullanıcıyı ray seçimine geri göndermek,
            bota gitmesi gerektiğini unutturur. */}
        <div className="flex items-center justify-between gap-4">
          <div className="min-w-0">
            <h3 className="font-heading font-bold text-white text-[19px] leading-none">
              {expired ? 'Trade expired' : 'Collect your items'}
            </h3>
            <p className="text-[10px] text-zinc-500 mt-1 truncate">to {step.roblox_username}</p>
          </div>
          {!expired && left != null && (
            <span className={`shrink-0 mm-num font-bold text-lg
              ${left <= 30 ? 'text-[#E5484D]' : 'text-white'}`}>{mmss(left)}</span>
          )}
        </div>

        {expired ? (
          <div className="rounded-2xl border border-[#2A2D31] bg-[#15171a] p-5 text-center
                          space-y-3">
            <Clock className="w-7 h-7 text-zinc-600 mx-auto" />
            <p className="text-sm text-zinc-300 leading-relaxed">
              The bot stopped waiting. Your balance will be refunded automatically.
            </p>
            <button onClick={() => cancel(step.trade_id)} disabled={busy}
              data-testid="mm2-wd-retry"
              className="w-full py-3.5 rounded-xl font-heading font-bold text-sm uppercase
                         tracking-wider bg-gradient-to-r from-[#15913f] to-[#16A34A]
                         hover:from-[#16A34A] hover:to-[#2ecf6e] text-white transition-all
                         shadow-lg transform active:scale-95 disabled:opacity-50
                         focus-visible:outline-none focus-visible:ring-2
                         focus-visible:ring-white/70
                         inline-flex items-center justify-center gap-2">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" />
                    : <RotateCcw className="w-4 h-4" />}
              Refund now
            </button>
          </div>
        ) : (
          <>
            <div className="rounded-2xl border border-[#2A2D31] bg-[#15171a] p-4 space-y-3">
              <div className="flex items-center gap-2.5">
                <span className={`w-2 h-2 rounded-full shrink-0 ${step.bot_online
                  ? 'bg-[#2ecf6e] shadow-[0_0_8px_#2ecf6e]' : 'bg-zinc-600'}`} />
                <span className="font-bold text-white text-sm truncate flex-1">
                  {step.bot_roblox || step.bot_name}
                </span>
                {step.leg_total > 1 && (
                  <span className="text-[10px] font-black text-zinc-400 shrink-0">
                    {(step.leg_index || 0) + 1}/{step.leg_total}
                  </span>
                )}
              </div>

              {!!(step.items || []).length && (
                <div className="flex flex-wrap gap-1.5">
                  {step.items.map((n, i) => (
                    <span key={i} className="text-[11px] font-bold text-zinc-300 bg-[#0E0F11]
                                             border border-[#22252a] rounded-lg px-2 py-1">
                      {n}
                    </span>
                  ))}
                </div>
              )}

              {step.join_link && (
                <a href={step.join_link} target="_blank" rel="noopener noreferrer"
                  data-testid="mm2-wd-join"
                  className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl
                             font-heading font-bold text-sm uppercase tracking-wider
                             bg-gradient-to-r from-[#15913f] to-[#16A34A]
                             hover:from-[#16A34A] hover:to-[#2ecf6e] text-white
                             transition-all shadow-lg transform active:scale-95
                             focus-visible:outline-none focus-visible:ring-2
                             focus-visible:ring-white/70">
                  <ExternalLink className="w-4 h-4" /> Join server
                </a>
              )}
            </div>

            {/* ⚠️ ÇOK BOTLU ÇEKİM AÇIKÇA SÖYLENİR: kullanıcı ilk takası
                kabul edip "bitti" sanarsa ikinci bota hiç gitmez. */}
            {step.leg_total > 1 && (
              <p className="text-[11px] text-zinc-400 text-center">
                Split across {step.leg_total} bots — the next one opens automatically.
              </p>
            )}

            <StepList active={step.status === 'queued' ? 0 : 1} />

            {step.status === 'queued' && (
              <button onClick={() => cancel(step.trade_id)} disabled={busy}
                data-testid="mm2-wd-cancel"
                className="w-full py-2.5 text-xs font-bold text-zinc-500 hover:text-rose-400
                           transition-colors inline-flex items-center justify-center gap-1.5">
                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      : <X className="w-3.5 h-3.5" />}
                Cancel &amp; refund
              </button>
            )}
          </>
        )}
      </div>
    );
  }

  /* ══════════════════════════════════ MAĞAZA ═══════════════════════════ */
  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-zinc-600" />
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="mm2-wd-shop">
      {/* ══════════════════════════════════════════════════════════════════
          BAŞLIK SATIRI — GERİ + BAŞLIK + BAKİYE, HEPSİ TEK SATIRDA
          ⚠️ ESKİDEN "← Back" MODALIN GÖVDESİNDE, KENDİ SATIRINDA DURUYORDU.
          Altında "Withdraw items", onun altında "Prices include a 5% fee",
          sağda bakiye… Üst üste üç satır ve ikisi neredeyse boş. Dar bir
          cüzdan penceresinde bu, mağaza ızgarasından çalınan ~90px demek.
          Üçü tek satırda: sol tarafta geri + başlık, sağda bakiye. Satır
          dolu, hiyerarşi net, mağaza yukarı geliyor.
          ══════════════════════════════════════════════════════════════════ */}
      <div className="flex items-center gap-3">
        {onBack && <BackButton onClick={onBack} testId="mm2-wd-back" />}
        <div className="min-w-0 flex-1">
          <h3 className="font-heading font-bold text-white text-[19px] leading-none truncate">
            Withdraw items
          </h3>
          {/* Komisyon başlığın ALTINDA ama AYNI bloğun içinde: ayrı bir
              satır açmıyor, başlığın alt satırı olarak okunuyor. */}
          <p className="text-[10px] text-zinc-500 mt-1 truncate">
            {markup > 0 ? `Prices include a ${markup}% fee` : 'Live bot stock'}
          </p>
        </div>
        <div className="text-right shrink-0">
          <p className="mm-num font-bold text-white text-[15px] inline-flex items-center
                        gap-1.5 leading-none">
            {fmtFull(free)}<MmCoinIcon size={14} />
          </p>
          {locked > 0 ? (
            <p className="text-[10px] text-[#F5A524] font-bold mt-1.5 inline-flex items-center
                          gap-1 justify-end">
              <Lock className="w-2.5 h-2.5" />{fmtFull(locked)} locked
            </p>
          ) : (
            <p className="mm-eyebrow mt-1.5">Available</p>
          )}
        </div>
      </div>

      {locked > 0 && (
        <div className="rounded-xl border border-amber-500/25 bg-amber-500/10 px-3.5 py-2.5
                        flex items-center gap-2.5">
          <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0" />
          <p className="text-[11px] text-amber-100/85 leading-snug">
            Wager <strong>{fmtFull(locked)}</strong> to unlock the rest of your balance.
          </p>
        </div>
      )}

      {paused && (
        <div className="rounded-xl border border-rose-500/25 bg-rose-500/10 px-3.5 py-2.5
                        flex items-center gap-2.5">
          <AlertTriangle className="w-4 h-4 text-rose-400 shrink-0" />
          <p className="text-[11px] text-rose-100/85">Withdrawals are paused right now.</p>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════
          TESLİMAT HESABI — KALICI, BÜYÜK, DOĞRUDAN YAZILABİLİR
          ══════════════════════════════════════════════════════════════════
          ⚠️ ESKİDEN SEPETİN İÇİNDE, 11px'LİK BİR SATIRDA VE KALEM İKONUNA
          BASINCA AÇILAN BİR ALANDI. İki sorunu vardı:

            1) DÜZENLENEBİLİR OLDUĞU ANLAŞILMIYORDU. Başka bir Roblox
               hesabına göndermek isteyen kullanıcı için gri bir metinden
               farkı yoktu. Eşyalar yanlış hesaba gidince geri dönüşü YOK.
            2) ÇOK GEÇ GÖRÜNÜYORDU. Yalnızca sepet dolunca, yeşil butonun
               hemen üstünde beliriyordu. Karar anında fark edilen bir alan
               acele edilerek atlanır.

          Artık alışverişten ÖNCE, tam genişlikte, gerçek bir input olarak
          duruyor. Kullanıcı hedefi baştan belirliyor; sepette de yalnızca
          okunabilir bir teyit satırı görüyor.
          ══════════════════════════════════════════════════════════════════ */}
      <div>
        <div className="flex items-baseline justify-between mb-1.5">
          <label htmlFor="mm2-wd-deliver" className="mm-eyebrow">Deliver to</label>
          {nameOk && linkedName
            && name.trim().toLowerCase() !== linkedName.trim().toLowerCase() && (
            /* ⚠️ Varsayılandan SAPILDIYSA açıkça söyle. Sessizce başka bir
               hesaba göndermek, kullanıcının fark etmeden yapabileceği en
               pahalı hata. */
            <span className="text-[10px] font-bold text-[#F5A524]">
              not your linked account
            </span>
          )}
        </div>
        <div className="relative">
          <input
            id="mm2-wd-deliver"
            value={name}
            onChange={(e) => setName(e.target.value.replace(/[^A-Za-z0-9_]/g, '').slice(0, 20))}
            placeholder="Roblox username"
            spellCheck={false} autoComplete="off"
            aria-label="Roblox username to deliver items to"
            data-testid="mm2-wd-username"
            className={`mm-focus w-full px-4 py-3.5 rounded-xl bg-[#15171a]
              text-white font-bold outline-none border transition-colors
              placeholder:text-zinc-600 placeholder:font-medium
              ${nameOk ? 'border-[#2A2D31] focus:border-[#B059E8]'
                       : 'border-[#F5A524]/50 focus:border-[#F5A524]'}`}
          />
        </div>
        <p className={`mt-1.5 text-[11px] ${nameOk ? 'text-zinc-500' : 'text-[#F5A524] font-semibold'}`}>
          {nameOk
            ? 'Items are traded to this account. Type a different one to send elsewhere.'
            : '3–20 characters — letters, numbers and _ only.'}
        </p>
      </div>

      {/* Arama + nadirlik */}
      <div className="flex gap-2">
        <div className="relative flex-1 min-w-0">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-500" />
          <input value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search" aria-label="Search items" data-testid="mm2-wd-search"
            className="mm-focus w-full pl-9 pr-3 py-2.5 rounded-xl bg-[#15171a] border border-[#2A2D31]
                       text-white text-sm font-medium outline-none focus:border-[#3a3d43]
                       placeholder:text-zinc-600 transition-colors" />
        </div>
        <select value={rarity} onChange={(e) => setRarity(e.target.value)}
          aria-label="Filter by rarity" data-testid="mm2-wd-rarity"
          className="mm-focus shrink-0 px-3 py-2.5 rounded-xl bg-[#15171a] border border-[#2A2D31]
                     text-white text-xs font-bold outline-none focus:border-[#3a3d43]
                     cursor-pointer transition-colors">
          <option value="all">All</option>
          {rarities.map(k => <option key={k} value={k}>{RARITY[k].label}</option>)}
        </select>
      </div>

      {/* Izgara — TAM GENİŞLİK */}
      {list.length === 0 ? (
        <div className="rounded-[14px] border border-dashed border-[#2A2D31] py-14
                        grid place-items-center text-center px-6">
          {/* Boş ekran bir davettir, bir özür değil: aramada sonuç yoksa
              nötr bir ikon, kasa boşsa oyunun kendi logosu — "buraya
              oyuncular eşya yatırınca dolar" mesajını görselle söylüyor. */}
          {items.length
            ? <PackageOpen className="w-7 h-7 text-zinc-600 mb-3" />
            : <Mm2Logo size={40} className="mb-3 opacity-40" alt="" />}
          <p className="font-heading font-bold text-zinc-300 text-base">
            {items.length ? 'Nothing matches' : 'Vault is empty'}
          </p>
          <p className="text-[11px] text-zinc-500 mt-1">
            {items.length ? 'Try another search.' : 'Stock comes from player deposits.'}
          </p>
        </div>
      ) : (
        /* ⚠️ JSX YORUMU DEĞİL, DÜZ BLOK YORUMU. Üçlü operatörün ifade
           konumundayız; buraya `{/* ... *\/}` yazmak iki ayrı ifade demek
           ve derleyici "Unexpected token" veriyor. Yorumun kendisi:
           SÜTUN SAYISI ARTTI (3→4, 4→5/6). Kartı küçültmenin doğru yolu
           sabit piksel boyutu vermek değil, ızgaraya daha çok sütun
           koymak — kart genişliği kabın genişliğinden türeyince panel dar
           da olsa geniş de olsa düzen bozulmuyor. */
        <div className="mm-scroll grid grid-cols-4 sm:grid-cols-5 lg:grid-cols-6 gap-1.5
                        max-h-[46vh] overflow-y-auto pr-1.5 -mr-1.5">
          {list.map(it => (
            <ShopCard key={it.item_id} item={it} count={countOf(it.item_id)}
              disabled={paused} affordable={(Number(it.price) || 0) <= free}
              onPick={toggle} />
          ))}
        </div>
      )}

      {/* ══ SEÇİM ÖZETİ — ALTA DOKUNUR ══
          ⚠️ Yalnızca seçim varken görünür: boş bir sepet kutusu, ekranın
          dörtte birini hiçbir şey söylemeden kaplıyordu. */}
      {picked.length > 0 && (
        <div className="mm-panel mm-chroma-edge p-3 space-y-3" data-testid="mm2-wd-cart">
          {/* Seçilenler — yatay şerit */}
          <div className="flex items-center gap-2 overflow-x-auto pb-1">
            {picked.map((id, idx) => {
              const it = byId[id];
              if (!it) return null;
              return (
                <button key={`${id}-${idx}`}
                  onClick={() => setPicked(p => p.filter((_, i) => i !== idx))}
                  aria-label={`Remove ${it.name}`}
                  className="group relative shrink-0 w-12 h-12 rounded-lg bg-[#0E0F11]
                             border border-[#22252a] grid place-items-center p-1
                             hover:border-rose-500/50 transition-colors
                             focus-visible:outline-none focus-visible:ring-2
                             focus-visible:ring-rose-400/60">
                  <ItemArt item={it} />
                  <span className="absolute inset-0 rounded-lg bg-rose-500/20 opacity-0
                                   group-hover:opacity-100 grid place-items-center
                                   transition-opacity">
                    <X className="w-4 h-4 text-white" strokeWidth={3} />
                  </span>
                </button>
              );
            })}
            {picked.length < MAX_PICK && (
              <span className="shrink-0 w-12 h-12 rounded-lg border border-dashed
                               border-[#2A2D31] grid place-items-center text-[10px]
                               font-black text-zinc-600">
                {MAX_PICK - picked.length}
              </span>
            )}
          </div>

          {/* ══ SON TEYİT — DÜZENLENEMEZ, SADECE HATIRLATIR ══
              Giriş alanı artık yukarıda, aramanın üstünde. Burada yalnızca
              "nereye gidiyor" yazıyor: kullanıcı yeşil butona basmadan önce
              hedefi son bir kez görsün diye. Aynı alanı iki yerde
              düzenlenebilir yapmak, hangisinin geçerli olduğu sorusunu
              doğurur. */}
          <div className="flex items-center gap-2 text-[11px] px-0.5">
            <span className="text-zinc-500 shrink-0">Delivering to</span>
            <span className={`font-bold truncate ${nameOk ? 'text-zinc-200' : 'text-[#F5A524]'}`}>
              {name || '— no username set'}
            </span>
          </div>

          {/* Toplam + eylem */}
          <div className="flex items-center justify-between gap-3 pt-2
                          border-t border-[#22252a]">
            <div className="min-w-0">
              <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-black">
                Total
              </p>
              <p className={`mm-num font-bold text-lg inline-flex items-center gap-1.5
                leading-none ${enough ? 'text-white' : 'text-[#E5484D]'}`}>
                {fmtFull(total)}<MmCoinIcon size={14} />
              </p>
            </div>
            <button onClick={submit}
              disabled={!enough || submitting || paused || !nameOk}
              data-testid="mm2-wd-submit"
              className="shrink-0 px-6 py-3 rounded-xl font-heading font-bold text-sm
                         uppercase tracking-wider transition-all shadow-lg
                         bg-gradient-to-r from-[#15913f] to-[#16A34A]
                         hover:from-[#16A34A] hover:to-[#2ecf6e] text-white
                         disabled:opacity-50 disabled:grayscale transform active:scale-95
                         focus-visible:outline-none focus-visible:ring-2
                         focus-visible:ring-white/70
                         inline-flex items-center justify-center gap-2">
              {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Starting…</>
                          : enough ? 'Withdraw' : 'Not enough'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Mm2WithdrawPanel;
